// Shown on every page while a cancelled plan is still running out its paid
// month: when it ends, and one tap to keep it. In the last 3 days it offers
// the lifetime price instead (the flow's secret price, when they qualify).
// Before this, the only way back was a Reactivate button in Settings, and 3%
// of cancels were ever undone. Server: pages/api/cancel-flow/ending.ts.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { getIdToken } from "../../utils/firebaseAuth";
import { X } from "lucide-react";
import { useAuth } from "../../context/AuthProvider";

/* eslint-disable @typescript-eslint/no-explicit-any */

type Ending = {
  endsAt: number;
  price: string;
  offer: { price: string; was: string } | null;
};

// One check per tab every half hour, not one per page.
const TTL_MS = 30 * 60_000;
const cacheKey = (uid: string) => `jammable:plan-ending:${uid}`;
const HIDDEN_KEY = "jammable:plan-ending:hidden";

const read = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Private mode: it just checks again.
  }
};

const post = async (body: Record<string, unknown>) => {
  const token = await getIdToken();
  return (
    await axios.post("/api/cancel-flow/ending", body, {
      headers: { Authorization: token },
    })
  ).data;
};

const longDate = (t: number) =>
  new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
const weekday = (t: number) =>
  new Date(t).toLocaleDateString("en-GB", { weekday: "long" });

export default function PlanEndingBanner() {
  const { user } = useAuth() as any;
  const uid: string | undefined = user?.firebaseUid;
  const subscribed = [user?.subscriptions ?? []]
    .flat()
    .some((s: any) => s?.status === "active");
  const [ending, setEnding] = useState<Ending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!uid || !subscribed || read(HIDDEN_KEY)) return;
    let cancelled = false;
    const load = async (): Promise<Ending | null> => {
      try {
        const c = JSON.parse(read(cacheKey(uid)) ?? "null");
        if (c && Date.now() - c.at < TTL_MS) return c.ending;
      } catch {
        // Check again.
      }
      const d = await post({ action: "check" });
      write(
        cacheKey(uid),
        JSON.stringify({ at: Date.now(), ending: d.ending }),
      );
      return d.ending;
    };
    load()
      .then((e) => !cancelled && setEnding(e))
      .catch((e) => console.warn("plan ending: check failed", e));
    return () => {
      cancelled = true;
    };
  }, [uid, subscribed]);

  if (hidden || !ending) return null;

  const keep = async (action: "keep" | "price") => {
    setBusy(true);
    setError(null);
    try {
      const d = await post({ action });
      if (uid) write(cacheKey(uid), null);
      setDone(
        (action === "price"
          ? `Done. From ${longDate(d.renewsAt)} you pay ${d.price} a month, for as long as you stay subscribed.`
          : `You're staying. Your plan renews on ${longDate(d.renewsAt)}.`) +
          (d.test ? " (Test: nothing changed in Stripe.)" : ""),
      );
    } catch (e: any) {
      setError(
        e?.response?.data?.message ??
          "That didn't work. Try again in a moment.",
      );
    }
    setBusy(false);
  };

  const hide = () => {
    write(HIDDEN_KEY, "1");
    setHidden(true);
  };

  const offer = ending.offer;
  return (
    <div className="tw-mx-auto tw-w-full tw-max-w-[1200px] tw-px-4 tw-pt-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-rounded-[20px] tw-bg-jam-ink tw-py-3 tw-pl-4 tw-pr-2 tw-text-white">
        <p className="tw-m-0 tw-min-w-[200px] tw-flex-1 tw-text-sm tw-font-bold">
          {done ??
            (offer ? (
              <>
                Your plan ends on {weekday(ending.endsAt)}.{" "}
                <span className="tw-text-white/75">
                  Keep everything for {offer.price} a month instead of{" "}
                  {offer.was}, for life.
                </span>
              </>
            ) : (
              <>
                Your plan ends on {longDate(ending.endsAt)}.{" "}
                <span className="tw-text-white/75">
                  After that you can&apos;t make covers.
                </span>
              </>
            ))}
          {error && (
            <span className="tw-block tw-text-[13px] tw-text-red-300">
              {error}
            </span>
          )}
        </p>
        {!done && (
          <button
            type="button"
            disabled={busy}
            onClick={() => keep(offer ? "price" : "keep")}
            className="tw-flex tw-min-h-[40px] tw-cursor-pointer tw-items-center tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-4 tw-text-[13px] tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-bg-[#C4EE00] disabled:tw-cursor-wait disabled:tw-opacity-60"
          >
            {busy
              ? "One moment..."
              : offer
                ? `Keep it for ${offer.price}`
                : "Keep my plan"}
          </button>
        )}
        <button
          type="button"
          onClick={hide}
          aria-label="Hide"
          className="tw-flex tw-h-10 tw-w-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-white/70 hover:tw-text-white"
        >
          <X size={16} strokeWidth={2.6} />
        </button>
      </div>
    </div>
  );
}
